import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AuthModal } from "@/components/AuthModal";
import { Mountain, Search, Menu, User, LogOut, LayoutDashboard, Luggage, MessageSquare } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";

export function Navbar({ onSearchClick }) {
  const { user, logout } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [defaultHost, setDefaultHost] = useState(false);
  const navigate = useNavigate();

  const openAuth = (host = false) => { setDefaultHost(host); setAuthOpen(true); };

  const handleHost = () => {
    if (!user) return openAuth(true);
    navigate("/gazda");
  };

  return (
    <header className="sticky top-0 z-50 bg-background/90 backdrop-blur-md border-b border-border/60 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-brand-logo">
          <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center">
            <Mountain className="h-5 w-5 text-primary-foreground" />
          </div>
          <span className="font-heading text-2xl font-bold text-primary hidden sm:block leading-none">
            Airbn<span className="text-accent">mountain</span>
          </span>
        </Link>

        <button onClick={onSearchClick} data-testid="nav-search-button"
                className="hidden md:flex items-center gap-3 rounded-full border border-border bg-card pl-5 pr-2 py-2 shadow-sm hover:shadow-md transition-shadow">
          <span className="text-sm font-medium">Oriunde în Apuseni</span>
          <span className="h-4 w-px bg-border" />
          <span className="text-sm text-muted-foreground">Adaugă date</span>
          <span className="h-8 w-8 rounded-full bg-accent flex items-center justify-center">
            <Search className="h-4 w-4 text-accent-foreground" />
          </span>
        </button>

        <div className="flex items-center gap-2">
          <Link to="/experiente" className="hidden sm:block text-sm font-medium px-4 py-2 rounded-full hover:bg-muted transition-colors" data-testid="nav-experiences-link">
            Experiențe
          </Link>
          <button onClick={handleHost} data-testid="nav-host-dashboard-link"
                  className="text-sm font-medium px-4 py-2 rounded-full hover:bg-muted transition-colors">
            {user?.is_host ? "Panou gazdă" : "Devino gazdă"}
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-full border border-border pl-3 pr-1.5 py-1.5 hover:shadow-md transition-shadow" data-testid="nav-user-menu-button">
                <Menu className="h-4 w-4" />
                <Avatar className="h-7 w-7">
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                    {user ? user.name?.[0]?.toUpperCase() || "U" : <User className="h-4 w-4" />}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {user ? (
                <>
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium truncate">{user.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/calatorii")} data-testid="menu-trips">
                    <Luggage className="h-4 w-4 mr-2" /> Călătoriile mele
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/mesaje")} data-testid="menu-messages">
                    <MessageSquare className="h-4 w-4 mr-2" /> Mesaje
                  </DropdownMenuItem>
                  {user.is_host && (
                    <DropdownMenuItem onClick={() => navigate("/gazda")} data-testid="menu-dashboard">
                      <LayoutDashboard className="h-4 w-4 mr-2" /> Panou gazdă
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                    <LogOut className="h-4 w-4 mr-2" /> Deconectare
                  </DropdownMenuItem>
                </>
              ) : (
                <>
                  <DropdownMenuItem onClick={() => openAuth(false)} data-testid="menu-login">
                    Autentificare
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => openAuth(false)} data-testid="menu-register">
                    Înregistrare
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => openAuth(true)} data-testid="menu-become-host">
                    Devino gazdă
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} defaultHost={defaultHost} />
    </header>
  );
}
